/**
 * ClassSelectionStep - Step 1: View and select classes (supports siblings with bundle discounts)
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, ArrowRight, Sparkles, LockKeyhole, Tag, Percent, User } from 'lucide-react';

// Bundle discount rules: Any Ballet/Tap + Street Dance combo = £45 each instead of £54
const BUNDLE_ORIGINAL_PRICE = 54;
const BUNDLE_DISCOUNTED_PRICE = 45;

// Check if a class qualifies for bundle pricing (any ballet/tap or street dance class)
const isBundleClass = (className) => {
  const lower = (className || '').toLowerCase();
  return lower.includes('ballet') || lower.includes('street dance');
};

// Check if student has both classes for bundle
const hasBundlePair = (studentClasses) => {
  if (!studentClasses || studentClasses.length < 2) return false;
  const classNames = studentClasses.map(c => c.name?.toLowerCase() || '');
  const hasBalletTap = classNames.some(n => n.includes('ballet/tap') || n.includes('ballet'));
  const hasStreet = classNames.some(n => n.includes('street'));
  return hasBalletTap && hasStreet;
};

// Calculate price with bundle, sibling discounts, and mid-term pro-rating
const calculateClassPrice = (cls, studentClasses, siblingDiscountPercent, isMidTerm = false, weeksRemaining = null, termLength = null) => {
  // Subscription classes (e.g. Primatotterina, billed monthly via Stripe)
  // do NOT get pro-rated or bundled. They keep their monthly price.
  // Sibling discount STILL applies to the first month though.
  const isSubscriptionClass = Boolean(cls?.is_subscription) || cls?.price_period === 'month';

  if (isSubscriptionClass) {
    const monthly = Number(cls?.subscription_price ?? cls?.monthly_price ?? cls?.term_price ?? 0);
    let price = monthly;
    let discountNotes = [];
    if (siblingDiscountPercent > 0) {
      price = price * (1 - siblingDiscountPercent / 100);
      discountNotes.push(`${siblingDiscountPercent}% sibling discount`);
    }
    return {
      finalPrice: price,
      originalPrice: monthly,
      proRatedFromPrice: monthly,
      hasDiscount: discountNotes.length > 0,
      discountNotes,
      isSubscription: true,
    };
  }

  let price = cls.term_price || 0;
  let discountNotes = [];
  let proRatedFromPrice = price;
  
  // Apply mid-term pro-rating first
  if (isMidTerm && weeksRemaining && termLength && weeksRemaining < termLength) {
    const proRatedPrice = (price / termLength) * weeksRemaining;
    price = Math.round(proRatedPrice * 100) / 100;
    discountNotes.push(`Pro-rated (${weeksRemaining}/${termLength} weeks)`);
  }
  
  // Check for bundle pricing (applied AFTER pro-rating)
  const qualifiesForBundle = isBundleClass(cls.name) && hasBundlePair(studentClasses);
  if (qualifiesForBundle && cls.term_price === BUNDLE_ORIGINAL_PRICE) {
    // Apply bundle ratio to already pro-rated price
    const bundleRatio = BUNDLE_DISCOUNTED_PRICE / BUNDLE_ORIGINAL_PRICE;
    price = price * bundleRatio;
    discountNotes.push('Bundle discount');
  }
  
  // Apply sibling discount on top
  if (siblingDiscountPercent > 0) {
    const siblingDiscount = price * (siblingDiscountPercent / 100);
    price = price - siblingDiscount;
    discountNotes.push(`${siblingDiscountPercent}% sibling discount`);
  }
  
  return { 
    finalPrice: price, 
    originalPrice: cls.term_price || 0,
    proRatedFromPrice,
    hasDiscount: discountNotes.length > 0,
    discountNotes,
    isSubscription: false,
  };
};

// Price display with multiple discount breakdown
const PriceWithBreakdown = ({ originalPrice, finalPrice, hasDiscount, discountNotes }) => {
  if (!hasDiscount) {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
        £{(originalPrice || 0).toFixed(2)}
      </Badge>
    );
  }
  
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <span className="text-white/40 line-through text-sm">
          £{(originalPrice || 0).toFixed(2)}
        </span>
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
          £{finalPrice.toFixed(2)}
        </Badge>
      </div>
      {discountNotes.length > 0 && (
        <p className="text-green-400 text-xs flex items-center gap-1">
          <Percent className="w-3 h-3" />
          {discountNotes.join(' + ')}
        </p>
      )}
    </div>
  );
};

export const ClassSelectionStep = ({
  preSelectedClasses,
  preSelectedClassIds,
  classes,
  selectedClasses,
  onClassToggle,
  discountPercent,
  discountNote,
  studentName,
  allStudents = [],
  hasSiblings = false,
  calculateTotal,
  onNext,
  // Mid-term pro-rating props
  isMidTerm = false,
  weeksRemaining = null,
  termLength = null
}) => {
  const handleClassToggle = (classId) => {
    if (preSelectedClassIds.includes(classId)) {
      toast.info('This class was pre-selected by admin and cannot be removed.');
      return;
    }
    onClassToggle(classId);
  };

  // Group classes by student
  const groupClassesByStudent = () => {
    if (!hasSiblings || allStudents.length === 0) {
      // Single student - just return all classes
      return [{
        student: { student_name: studentName, discount_percent: discountPercent },
        classes: preSelectedClasses
      }];
    }
    
    // Multiple students - group by student_name from class data
    const groups = {};
    
    allStudents.forEach(student => {
      groups[student.student_name] = {
        student: student,
        classes: []
      };
    });
    
    preSelectedClasses.forEach(cls => {
      const studentName = cls.student_name;
      if (studentName && groups[studentName]) {
        groups[studentName].classes.push(cls);
      } else {
        // Fallback - add to first student
        const firstStudent = allStudents[0]?.student_name;
        if (firstStudent && groups[firstStudent]) {
          groups[firstStudent].classes.push(cls);
        }
      }
    });
    
    return Object.values(groups).filter(g => g.classes.length > 0);
  };

  const studentGroups = groupClassesByStudent();
  
  // Calculate totals with bundle, sibling discounts, and mid-term pro-rating
  const calculateTotalWithBundles = () => {
    let subtotal = 0;
    let finalTotal = 0;
    
    studentGroups.forEach(group => {
      const siblingDiscount = group.student.discount_percent || 0;
      // Per-student mid-term pro-rate (falls back to the link-level values).
      const sIsMid = group.student.is_mid_term ?? isMidTerm;
      const sWeeks = group.student.weeks_remaining ?? weeksRemaining;
      const sTerm  = group.student.term_length    ?? termLength;
      
      group.classes.forEach(cls => {
        subtotal += cls.term_price || 0;
        const pricing = calculateClassPrice(cls, group.classes, siblingDiscount, sIsMid, sWeeks, sTerm);
        finalTotal += pricing.finalPrice;
      });
    });
    
    // Add any additional selected classes (apply pro-rating but not sibling discount).
    // Uses the link-level pro-rate since these are not yet linked to a specific student.
    selectedClasses.forEach(classId => {
      const cls = classes.find(c => c.id === classId);
      if (cls) {
        subtotal += cls.term_price || 0;
        if (isMidTerm && weeksRemaining && termLength && weeksRemaining < termLength) {
          const proRatedPrice = ((cls.term_price || 0) / termLength) * weeksRemaining;
          finalTotal += Math.round(proRatedPrice * 100) / 100;
        } else {
          finalTotal += cls.term_price || 0;
        }
      }
    });
    
    return {
      subtotal,
      discount: subtotal - finalTotal,
      total: finalTotal
    };
  };
  
  const totals = calculateTotalWithBundles();

  // If ANY student has their own per-student pro-rate set, hide the shared
  // banner at the top — the per-student banner inside each card is more accurate.
  const anyPerStudentProRate = allStudents?.some(
    (s) => s && (s.is_mid_term != null || s.weeks_remaining != null)
  );

  return (
    <div className="space-y-6">
      {/* Mid-Term Pro-Rating Banner (link-level — hidden when any student has per-student prorate) */}
      {!anyPerStudentProRate && isMidTerm && weeksRemaining && termLength && weeksRemaining < termLength && (
        <Card className="bg-cyan-500/10 border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Tag className="w-6 h-6 text-cyan-400" />
              <div>
                <p className="text-cyan-400 font-semibold">
                  Mid-Term Pro-Rated Pricing
                </p>
                <p className="text-cyan-400/70 text-sm">
                  You're joining with {weeksRemaining} weeks remaining in the term. Your fees are calculated as {weeksRemaining}/{termLength} of the full price.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Siblings Banner */}
      {hasSiblings && allStudents.length > 1 && (
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-purple-400 font-semibold">
                  Registering {allStudents.length} Students
                </p>
                <p className="text-purple-400/70 text-sm">
                  {allStudents.map(s => s.student_name).join(', ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Classes grouped by student */}
      {studentGroups.map((group, groupIdx) => (
        <Card key={groupIdx} className="bg-emerald-500/10 border-emerald-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-400" />
              {group.student.student_name || 'Student'}
              {group.student.discount_percent > 0 && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 ml-2">
                  <Percent className="w-3 h-3 mr-1" />
                  {group.student.discount_percent}% sibling discount
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Per-student pro-rate scoped to this group's rendering
              const sIsMid = group.student.is_mid_term ?? isMidTerm;
              const sWeeks = group.student.weeks_remaining ?? weeksRemaining;
              const sTerm  = group.student.term_length    ?? termLength;
              const showStudentBanner = sIsMid && sWeeks && sTerm && sWeeks < sTerm && (
                group.student.is_mid_term != null ||
                group.student.weeks_remaining != null
              );
              return (
                <>
                  {showStudentBanner && (
                    <div className="mb-3 p-2 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                      <p className="text-cyan-300 text-xs flex items-center gap-2">
                        <Tag className="w-3.5 h-3.5" />
                        {group.student.student_name || 'This student'} is joining with {sWeeks} week{sWeeks === 1 ? '' : 's'} remaining — fees calculated as {sWeeks}/{sTerm} of the full price.
                      </p>
                    </div>
                  )}
                  {hasBundlePair(group.classes) && (
                    <div className="mb-3 p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                      <p className="text-yellow-400 text-sm flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Bundle discount applied! Ballet/Tap + Street Dance = £45 each
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    {group.classes.map((cls, idx) => {
                      const pricing = calculateClassPrice(cls, group.classes, group.student.discount_percent || 0, sIsMid, sWeeks, sTerm);
                
                return (
                  <div 
                    key={`${cls.id}-${idx}`}
                    className="p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <div>
                          <h4 className="text-white font-semibold">{cls.name}</h4>
                          <p className="text-white/50 text-sm">{cls.category}</p>
                          {cls.day_of_week !== undefined && cls.start_time && (
                            <p className="text-white/40 text-xs mt-1">
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][cls.day_of_week]} at {cls.start_time}
                            </p>
                          )}
                        </div>
                      </div>
                      <PriceWithBreakdown {...pricing} />
                    </div>
                  </div>
                );
              })}
            </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      ))}

      {/* No classes message */}
      {preSelectedClasses.length === 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-white">No classes have been pre-selected.</p>
            <p className="text-white/60 text-sm mt-2">
              You can select classes in the next steps or contact the school.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Additional Classes (Optional) */}
      {classes.length > 0 && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF007F]" />
              Add More Classes (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-white/60 mb-4">Would you like to enroll in any additional classes?</p>
            {hasSiblings && (
              <p className="text-yellow-400/70 text-sm mb-3 italic">
                Note: Additional classes are charged at full price. Discounts apply only to pre-assigned classes above.
              </p>
            )}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {classes.filter(cls => !preSelectedClassIds.includes(cls.id)).map(cls => (
                <div 
                  key={cls.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedClasses.includes(cls.id) 
                      ? 'bg-[#FF007F]/20 border-[#FF007F]' 
                      : 'bg-white/5 border-white/10 hover:border-white/30'
                  }`}
                  onClick={() => handleClassToggle(cls.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedClasses.includes(cls.id)}
                        onCheckedChange={() => handleClassToggle(cls.id)}
                        className="border-white/30 data-[state=checked]:bg-[#FF007F]"
                      />
                      <div>
                        <h4 className="text-white font-semibold">{cls.name}</h4>
                        <p className="text-white/50 text-sm">{cls.category}</p>
                      </div>
                    </div>
                    <Badge className="bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30">
                      £{(cls.term_price || 0).toFixed(2)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discount Banner */}
      {totals.discount > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Tag className="w-6 h-6 text-yellow-400" />
              <div>
                <p className="text-yellow-400 font-semibold">Discounts Applied!</p>
                <p className="text-yellow-400/70 text-sm">
                  You're saving £{totals.discount.toFixed(2)} with bundle and sibling discounts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <Card className="bg-[#FF007F]/10 border-[#FF007F]/30">
        <CardContent className="p-6">
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-white/70">Classes:</span>
              <span className="text-white">{preSelectedClasses.length + selectedClasses.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/70">Subtotal:</span>
              <span className="text-white">£{totals.subtotal.toFixed(2)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Total Savings:</span>
                <span>-£{totals.discount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between text-lg pt-3 border-t border-white/10">
            <span className="text-white font-semibold">Total:</span>
            <span className="text-[#FF007F] font-bold text-2xl">£{totals.total.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Continue Button */}
      <Button 
        onClick={onNext}
        className="w-full bg-[#FF007F] hover:bg-[#D6006A] text-white py-6"
        data-testid="step1-continue"
      >
        Continue <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default ClassSelectionStep;
